import type {
  IAssetSource,
  ResourceContext,
  TileReadOptions,
  TileResourceData,
} from '@/plugins/tiles/contracts'
import { TilesError, throwIfTilesAborted } from '@/plugins/tiles/TilesError'

/** 将调用方认证固定在最初的允许源站集合，外部文档只更换 documentUrl。 */
export function createResourceContext(
  documentUrl: string,
  request: {
    headers?: HeadersInit
    credentials?: RequestCredentials
    credentialOrigins?: readonly string[]
  } = {}
): ResourceContext {
  const url = new URL(documentUrl)
  return Object.freeze({
    documentUrl: url.href,
    headers: Object.freeze(Object.fromEntries(new Headers(request.headers).entries())),
    credentials: request.credentials ?? 'same-origin',
    credentialOrigins: Object.freeze(
      (request.credentialOrigins ?? [url.origin]).map((origin) => new URL(origin).origin)
    ),
  })
}

function requestError(url: string, message: string, retryable = false): TilesError {
  return new TilesError({
    code: 'http-error',
    stage: 'request',
    sourceUrl: url,
    message,
    retryable,
  })
}

/** 不借用 Three LoadingManager 的 HTTP 读取器，所有成本和取消向调用方显式返回。 */
export class HttpAssetSource implements IAssetSource {
  constructor(private readonly _fetch: typeof fetch = globalThis.fetch.bind(globalThis)) {}

  resolve(uri: string, context: ResourceContext): string {
    let url: URL
    try {
      url = new URL(uri, context.documentUrl)
    } catch (cause) {
      throw new TilesError(
        {
          code: 'invalid-input',
          stage: 'request',
          sourceUrl: context.documentUrl,
          message: `无效的 Tiles 资源 URI: ${uri}`,
        },
        { cause }
      )
    }
    if (!['http:', 'https:', 'data:', 'blob:'].includes(url.protocol)) {
      throw new TilesError({
        code: 'unsupported-capability',
        stage: 'request',
        sourceUrl: url.href,
        message: `Tiles HTTP 数据源不支持 ${url.protocol}`,
      })
    }
    url.hash = ''
    return url.href
  }

  async read(
    uri: string,
    context: ResourceContext,
    options: TileReadOptions = {}
  ): Promise<TileResourceData> {
    throwIfTilesAborted(options.signal)
    const url = this.resolve(uri, context)
    const limit = options.maxBytes ?? Infinity
    if (!(limit > 0) || (Number.isFinite(limit) && !Number.isSafeInteger(limit))) {
      throw new TilesError({
        code: 'invalid-input',
        stage: 'request',
        sourceUrl: url,
        message: 'Tiles 读取额度必须为正整数字节数',
      })
    }
    const origin = new URL(url).origin
    const allowedOrigins = context.credentialOrigins ?? [new URL(context.documentUrl).origin]
    const authenticated = allowedOrigins.includes(origin)
    const headers = new Headers(authenticated ? context.headers : undefined)
    headers.delete('Range')
    if (options.prefix && Number.isFinite(limit) && /^https?:/.test(url)) {
      headers.set('Range', `bytes=0-${limit - 1}`)
    }

    let response: Response
    try {
      response = await this._fetch(url, {
        headers,
        credentials: authenticated ? (context.credentials ?? 'same-origin') : 'omit',
        signal: options.signal,
        // 不在浏览器自动重定向中将 include 凭证传播给未经允许的源站。
        redirect: 'error',
      })
    } catch {
      throwIfTilesAborted(options.signal)
      throw requestError(url, `Tiles 资源请求失败: ${url}`, true)
    }
    if (!response.ok) {
      await response.body?.cancel().catch(() => {})
      throw requestError(
        url,
        `Tiles 资源 HTTP ${response.status}: ${url}`,
        response.status === 408 || response.status === 429 || response.status >= 500
      )
    }
    const range = response.headers.get('Content-Range')?.match(/^bytes (\d+)-(\d+)\/(\d+|\*)$/)
    if (
      response.status === 206 &&
      (!range ||
        Number(range[1]) !== 0 ||
        !Number.isSafeInteger(Number(range[2])) ||
        Number(range[2]) < 0 ||
        (range[3] !== '*' &&
          (!Number.isSafeInteger(Number(range[3])) || Number(range[3]) <= Number(range[2]))))
    ) {
      await response.body?.cancel().catch(() => {})
      throw requestError(url, `Tiles 资源返回无效的起始 Range: ${url}`)
    }
    const encoded = !['', 'identity'].includes(
      (response.headers.get('Content-Encoding') ?? '').trim().toLowerCase()
    )
    // fetch 的响应流已解压，而 Content-Length 通常仍描述压缩传输长度。
    const totalBytes = encoded
      ? undefined
      : range?.[3] && range[3] !== '*'
        ? Number(range[3])
        : response.status === 200 && response.headers.has('Content-Length')
          ? Number(response.headers.get('Content-Length'))
          : undefined
    if (totalBytes !== undefined && (!Number.isSafeInteger(totalBytes) || totalBytes < 0)) {
      await response.body?.cancel().catch(() => {})
      throw requestError(url, `Tiles 资源长度无效: ${url}`)
    }
    if (
      !options.prefix &&
      response.status === 206 &&
      (totalBytes === undefined || Number(range?.[2]) + 1 !== totalBytes)
    ) {
      await response.body?.cancel().catch(() => {})
      throw requestError(url, `Tiles 完整读取收到不完整 Range: ${url}`)
    }
    if (!options.prefix && totalBytes !== undefined && totalBytes > limit) {
      await response.body?.cancel().catch(() => {})
      throw this.limitError(url, limit)
    }

    const chunks: Uint8Array[] = []
    // 未压缩且长度已验证时只分配最终缓冲；流块直接写入，避免两次整包复制。
    const expectedLength = !encoded && response.status === 206 ? Number(range?.[2]) + 1 : totalBytes
    const direct =
      expectedLength !== undefined ? new Uint8Array(Math.min(expectedLength, limit)) : undefined
    let count = 0
    let complete = false
    const reader = response.body?.getReader()
    if (!reader) {
      throw requestError(url, `Tiles 资源没有可读取的响应流: ${url}`)
    }
    const onAbort = (): void => {
      void reader.cancel().catch(() => {})
    }
    options.signal?.addEventListener('abort', onAbort, { once: true })
    try {
      while (true) {
        throwIfTilesAborted(options.signal)
        const next = await reader.read()
        throwIfTilesAborted(options.signal)
        if (next.done) {
          if (expectedLength !== undefined && count !== expectedLength)
            throw requestError(url, `Tiles 资源实际长度与响应头不符: ${url}`)
          complete = response.status !== 206 || totalBytes === count
          break
        }
        const remaining = limit - count
        if (next.value.byteLength > remaining && !options.prefix) {
          throw this.limitError(url, limit)
        }
        const chunk = next.value.subarray(0, remaining)
        if (direct) {
          if (count + chunk.byteLength > direct.byteLength)
            throw requestError(url, `Tiles 资源超出声明长度: ${url}`)
          direct.set(chunk, count)
        } else chunks.push(chunk)
        count += chunk.byteLength
        if (count === limit && options.prefix) {
          complete = totalBytes !== undefined && totalBytes === count
          break
        }
      }
    } finally {
      options.signal?.removeEventListener('abort', onAbort)
      await reader.cancel().catch(() => {})
      reader.releaseLock()
    }
    throwIfTilesAborted(options.signal)
    const data = direct ?? new Uint8Array(count)
    let offset = 0
    for (const chunk of chunks) {
      data.set(chunk, offset)
      offset += chunk.byteLength
    }
    return {
      url: response.url || url,
      data: data.byteLength === count ? data.buffer : data.buffer.slice(0, count),
      complete,
      totalBytes,
      contentType: response.headers.get('Content-Type') ?? '',
    }
  }

  private limitError(url: string, limit: number): TilesError {
    return new TilesError({
      code: 'limit-exceeded',
      stage: 'request',
      sourceUrl: url,
      message: `Tiles 资源超出读取额度 ${limit} 字节: ${url}`,
    })
  }
}
